import {
  DateTime,
} from 'luxon';

import {
  getLuxonDate,
} from '@/composables/date.js';

import {
  GLOBAL_CONSTANTS,
  QUERY_CONSTANTS,
} from '@/constants';

import {
  company_industry_class_list,
  company_size_list,
  job_type_list,
  news_tag_list,
} from '@/constants/referentials';

import {
  business_alert_repository,
  query_repository,
} from '@/repositories';

const state = () => ({
  criteria_company_industry_class_id_list: [],
  criteria_company_size_id_list: [],
  criteria_facet_list: [],
  criteria_facet_range: [],
  criteria_job_type_id_list: [],
  criteria_limit: GLOBAL_CONSTANTS.BUSINESS_ALERTS_LIMIT,
  criteria_news_publication_date_max: DateTime
    .now()
    .endOf('day')
    .toUTC()
    .toISO(),
  criteria_news_publication_date_min: DateTime
    .now()
    .minus({ weeks: 1 })
    .startOf('day')
    .toUTC()
    .toISO(),
  criteria_news_tag_id_list: [],
  criteria_offset: 0,
  criteria_query_id_list: [],
  criteria_quick_search: '',
  facet_list: [],
  facet_range: [],
  filters: {
    company_size_list: [],
    job_type_list: [],
    queries: {
      alert_list: [],
      segment_list: [],
    },
    news_type_list: [],
    sector_list: [],
  },
  loading_search_news_and_facets: false,
  news_list: [],
  query_list: [],
  query_alert_list: [],
  query_contacts_and_accounts_list: [],
  query_network_list: [],
  reach_end_search_news_and_facets: false,
});

const getters = {
  criteriaCompanyIndustryClassIdList: (state) => state.criteria_company_industry_class_id_list,
  criteriaCompanySizeIdList: (state) => state.criteria_company_size_id_list,
  criteriaFacetList: (state) => state.criteria_facet_list,
  criteriaFacetRange: (state) => state.criteria_facet_range,
  criteriaJobTypeIdList: (state) => state.criteria_job_type_id_list,
  criteriaLimit: (state) => state.criteria_limit,
  criteriaNewsPublicationDateMax: (state) => state.criteria_news_publication_date_max,
  criteriaNewsPublicationDateMin: (state) => state.criteria_news_publication_date_min,
  criteriaNewsTagIdList: (state) => state.criteria_news_tag_id_list,
  criteriaOffset: (state) => state.criteria_offset,
  criteriaQueryIdList: (state) => state.criteria_query_id_list,
  criteriaQuickSearch: (state) => state.criteria_quick_search,
  facetList: (state) => state.facet_list,
  loadingSearchNewsAndFacets: (state) => state.loading_search_news_and_facets,
  newsList: (state) => state.news_list,
  queryList: (state) => state.query_list,
  queryAlertList: (state) => state.query_alert_list,
  queryContactsAndAccountsList: (state) => state.query_contacts_and_accounts_list,
  queryNetworkList: (state) => state.query_network_list,
  reachEndSearchNewsAndFacets: (state) => state.reach_end_search_news_and_facets,
};

const actions = {
  async getUserQueries({ commit, rootState }) {
    const response = await query_repository.searchUserQueryList(
      rootState.user.token_data.user_id,
      {
        query_type_id_list: [
          QUERY_CONSTANTS.TYPES.ALERT,
          QUERY_CONSTANTS.TYPES.SEGMENT_CONTACT,
          QUERY_CONSTANTS.TYPES.SEGMENT_ACCOUNT,
          QUERY_CONSTANTS.TYPES.ADDRESS_BOOK,
          QUERY_CONSTANTS.TYPES.CLIENTS,
          QUERY_CONSTANTS.TYPES.PROSPECTS,
          QUERY_CONSTANTS.TYPES.COMPETITORS,
          QUERY_CONSTANTS.TYPES.NETWORK_SCHOOL_CONTACT,
          QUERY_CONSTANTS.TYPES.NETWORK_COMPANY_CONTACT,
          QUERY_CONSTANTS.TYPES.KEY_ACCOUNTS,
        ],
      }
    );

    const isUsableQuery = (query) => {
      const account_segment_id_list = [
        QUERY_CONSTANTS.TYPES.SEGMENT_ACCOUNT,
        QUERY_CONSTANTS.TYPES.CLIENTS,
        QUERY_CONSTANTS.TYPES.PROSPECTS,
        QUERY_CONSTANTS.TYPES.COMPETITORS,
        QUERY_CONSTANTS.TYPES.KEY_ACCOUNTS,
      ];

      const contact_segment_id_list = [
        QUERY_CONSTANTS.TYPES.SEGMENT_CONTACT,
        QUERY_CONSTANTS.TYPES.ADDRESS_BOOK,
      ];

      if (account_segment_id_list.indexOf(query.query_type.id) > -1) {
        return (
          query.criteria
          && Array.isArray(query.criteria.company_id_list)
          && query.criteria.company_id_list.length > 0
        );
      }

      if (contact_segment_id_list.indexOf(query.query_type.id) > -1) {
        return (
          query.criteria
          && Array.isArray(query.criteria.person_id_list)
          && query.criteria.person_id_list.length > 0
        );
      }

      if (query.criteria && query.criteria.constructor === Object) {
        const validated_criteria = {};

        Object.keys(query.criteria).forEach((x) => {
          if (Array.isArray(query.criteria[x]) && query.criteria[x].length > 0) {
            validated_criteria[x] = query.criteria[x].filter((x) => !!x);

            if (validated_criteria[x].length === 0) {
              delete validated_criteria[x];
            }
          }

          if (!Array.isArray(query.criteria[x]) && !!query.criteria[x]) {
            validated_criteria[x] = query.criteria[x];
          }
        });

        return Object.keys(validated_criteria).length > 0;
      }

      return false;
    };

    const query_list = response.query_list.filter(isUsableQuery);

    commit('set_query_list', query_list);

    const query_alert_list = query_list
      .filter((query) => query.query_type.id === QUERY_CONSTANTS.TYPES.ALERT
        && query.alert === true
      );

    const query_contacts_and_accounts_list = query_list
      .filter((query) => [
        QUERY_CONSTANTS.TYPES.SEGMENT_CONTACT,
        QUERY_CONSTANTS.TYPES.SEGMENT_ACCOUNT,
        QUERY_CONSTANTS.TYPES.ADDRESS_BOOK,
        QUERY_CONSTANTS.TYPES.CLIENTS,
        QUERY_CONSTANTS.TYPES.PROSPECTS,
        QUERY_CONSTANTS.TYPES.COMPETITORS,
        QUERY_CONSTANTS.TYPES.KEY_ACCOUNTS,
      ].includes(query.query_type.id));

    const query_network_list = query_list
      .filter((query) => [
        QUERY_CONSTANTS.TYPES.NETWORK_SCHOOL_CONTACT,
        QUERY_CONSTANTS.TYPES.NETWORK_COMPANY_CONTACT,
      ].includes(query.query_type.id));

    commit('set_query_alert_list', query_alert_list);
    commit('set_query_contacts_and_accounts_list', query_contacts_and_accounts_list);
    commit('set_query_network_list', query_network_list);

    return query_list;
  },
  async searchNews({ commit }, params) {
    const response = await business_alert_repository.searchNews(params);

    commit('set_news_list', response.news_list);

    return response.news_list;
  },
  async searchNewsAndFacets({ commit, state }) {
    commit('set_loading_search_news_and_facets', true);

    if (state.criteria_offset === 0) {
      commit('set_news_list', []);
    }

    const data = {
      limit: state.criteria_limit,
      news_publication_date_max: getLuxonDate(state.criteria_news_publication_date_max, {zone: 'utc'})
        .endOf('day')
        .toISO(),
      news_publication_date_min: getLuxonDate(state.criteria_news_publication_date_min, {zone: 'utc'})
        .startOf('day')
        .toISO(),
      offset: state.criteria_offset,
      sort: ['-publication_date'],
    };

    [
      'company_industry_class_id_list',
      'company_size_id_list',
      'job_type_id_list',
      'news_tag_id_list',
      'query_id_list',
    ].forEach((criteria) => {
      if (state[`criteria_${criteria}`].length > 0) {
        data[criteria] = state[`criteria_${criteria}`];
      }
    });

    if (state.criteria_quick_search.length > 0) {
      data.quick_search = state.criteria_quick_search;
    }

    const promise_list = [
      business_alert_repository.searchNews(data),
    ];

    if (state.criteria_offset === 0) {
      const data_facet = {
        ...data,
        facet_list: [],
      };

      delete data_facet.offset;

      const addQueryParamsForFacet = (key, collection) => {
        data_facet.facet_list.push({
          field: key,
          value_list: collection.map((item) => item.id),
          limit: 100,
        });
      };

      addQueryParamsForFacet('news_tag_id_list', news_tag_list);
      addQueryParamsForFacet('company_industry_class_id_list', company_industry_class_list);
      addQueryParamsForFacet('job_type_id_list', job_type_list.map((item) => ({
        id: item.job_class.id,
      })));
      addQueryParamsForFacet('company_size_id_list', company_size_list);
      addQueryParamsForFacet('query_id_list', state.query_list);

      promise_list.push(business_alert_repository.searchFacets(data_facet));
    }

    try {
      const [
        response_news,
        response_facet,
      ] = await Promise.all(promise_list);

      let news_list;

      if (state.criteria_offset === 0) {
        news_list = response_news.news_list;

        commit('set_facet_list', response_facet.facet_list);
      } else {
        news_list = state.news_list.concat(response_news.news_list);
      }

      if (response_news.news_list.length < state.criteria_limit) {
        commit('set_reach_end_search_news_and_facets', true);
      }

      commit('set_criteria_offset', state.criteria_offset + GLOBAL_CONSTANTS.BUSINESS_ALERTS_LIMIT);
      commit('set_news_list', news_list);
      commit('set_loading_search_news_and_facets', false);
    } catch (error) {
      console.error('searchNewsAndFacets_error', error);

      commit('set_loading_search_news_and_facets', false);
    }

    return;
  },
};

const mutations = {
  set_criteria_company_industry_class_id_list(state, criteria_company_industry_class_id_list) {
    state.criteria_company_industry_class_id_list = criteria_company_industry_class_id_list;
  },
  set_criteria_company_size_id_list(state, criteria_company_size_id_list) {
    state.criteria_company_size_id_list = criteria_company_size_id_list;
  },
  set_criteria_facet_list(state, criteria_facet_list) {
    state.criteria_facet_list = criteria_facet_list;
  },
  set_criteria_facet_range(state, criteria_facet_range) {
    state.criteria_facet_range = criteria_facet_range;
  },
  set_criteria_job_type_id_list(state, criteria_job_type_id_list) {
    state.criteria_job_type_id_list = criteria_job_type_id_list;
  },
  set_criteria_news_publication_date_max(state, criteria_news_publication_date_max) {
    state.criteria_news_publication_date_max = criteria_news_publication_date_max;
  },
  set_criteria_news_publication_date_min(state, criteria_news_publication_date_min) {
    state.criteria_news_publication_date_min = criteria_news_publication_date_min;
  },
  set_criteria_news_tag_id_list(state, criteria_news_tag_id_list) {
    state.criteria_news_tag_id_list = criteria_news_tag_id_list;
  },
  set_criteria_offset(state, criteria_offset) {
    state.criteria_offset = criteria_offset;
  },
  set_criteria_query_id_list(state, criteria_query_id_list) {
    state.criteria_query_id_list = criteria_query_id_list;
  },
  set_criteria_quick_search(state, criteria_quick_search) {
    state.criteria_quick_search = criteria_quick_search;
  },
  set_facet_list(state, facet_list) {
    state.facet_list = facet_list;
  },
  set_loading_search_news_and_facets(state, loading_search_news_and_facets) {
    state.loading_search_news_and_facets = loading_search_news_and_facets;
  },
  set_news_list(state, news_list) {
    state.news_list = news_list;
  },
  set_query_list(state, query_list) {
    state.query_list = query_list;
  },
  set_query_alert_list(state, query_alert_list) {
    state.query_alert_list = query_alert_list;
  },
  set_query_contacts_and_accounts_list(state, query_contacts_and_accounts_list) {
    state.query_contacts_and_accounts_list = query_contacts_and_accounts_list;
  },
  set_query_network_list(state, query_network_list) {
    state.query_network_list = query_network_list;
  },
  set_reach_end_search_news_and_facets(state, reach_end_search_news_and_facets) {
    state.reach_end_search_news_and_facets = reach_end_search_news_and_facets;
  },
};

export default {
  actions,
  getters,
  mutations,
  namespaced: true,
  state,
};
